import { useEffect, useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { AllSchemas } from "../schema";
import { Input } from "../CreditBalanceForm/Input";
import { zipCodeMask } from "@/app/utils/zipCodeFormater";
import { banks } from "@/app/utils/bank";
import { Summary } from "./summary";
import { Button } from "@/view/components/ui/button";
import { Landmark, Mail, MapPin } from "lucide-react";
import { Select } from "./select";
import { CreditCard } from "@/view/components/CreditCard";
import { useFinalizationForm } from "./useFinalizationForm";
import { orgs } from "@/app/utils/orgs";
import { uf } from "@/app/utils/uf";
import { rgMask } from "@/app/utils/rgNumberFormater";
import { dateMask } from "@/app/utils/dateFormater";
import { states } from "@/app/utils/states";
import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext";
import { PartnerType } from "@/app/utils/partners.enum";
import { getPartnerBgColor, getPartnerImage } from "@/app/utils/partners";
import { CreditPartner } from "../../useCreditController";
import { cn } from "@/app/utils/cn";
import { phoneMask } from "@/app/utils/phoneFormater";
import { useSelfHireRelationshipDegree } from "@/app/hooks/useSelfHireRelationshipDegree";
import { useSelfHireGenders } from "@/app/hooks/useSelfHireGenders";
import { useSelfHireMaritalStatus } from "@/app/hooks/useSelfHireMaritalStatus";

export const FinalizationForm = () => {
  const methods = useFormContext<AllSchemas>();
  const { handleSubmit, isLoading, banksCrefaz, handleCepRequest } =
    useFinalizationForm(methods);
  const { type } = useSelfHiringContext();
  const isCrefaz = type === PartnerType.CREFAZ;
  const { data: relationshipDegreeData } = useSelfHireRelationshipDegree({ enabled: isCrefaz });
  const { data: gendersData } = useSelfHireGenders({ enabled: isCrefaz });
  const { data: maritalStatusData } = useSelfHireMaritalStatus({ enabled: isCrefaz });
  const relationshipDegreeOptions =
    relationshipDegreeData?.data.map((relationship) => ({
      label: relationship.nome,
      value: String(relationship.id),
    })) ?? [];
  const genderOptions =
    gendersData?.data.map((gender) => ({
      label: gender.nome,
      value: String(gender.id),
    })) ?? [];
  const maritalStatusOptions =
    maritalStatusData?.data.map((status) => ({
      label: status.nome,
      value: String(status.id),
    })) ?? [];
  const bankOptions = useMemo(
    () => banks.map((bank) => ({ value: bank.value, label: `${bank.value} - ${bank.label}` })),
    [],
  );

  const selectedMaritalStatus = methods.watch("maritalStatus" as any);
  const requiresSpouseName =
    maritalStatusData?.data.find(
      (status) => String(status.id) === selectedMaritalStatus,
    )?.exigeConjuge ?? false;

  useEffect(() => {
    if (!requiresSpouseName) {
      methods.setValue("spouseName" as any, "");
    }
  }, [requiresSpouseName, methods]);

  const { fields, replace } = useFieldArray({
    control: methods.control as any,
    name: "contactReferences.referenceContact" as any,
  });

  useEffect(() => {
    if (isCrefaz && fields.length !== 2) {
      replace([
        { degree: "", name: "", phone: "" },
        { degree: "", name: "", phone: "" },
      ]);
    }
  }, [isCrefaz, fields.length, replace]);

  return (
    <div className="flex w-full flex-col gap-4">
      <div
        className="mx-auto mt-2 flex h-[72px] w-full shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: getPartnerBgColor(type as PartnerType) }}
      >
        <img
          src={getPartnerImage(type as CreditPartner)}
          alt={(type as string) ?? ""}
          className={cn(
            "h-14 object-contain",
            type === PartnerType.PAN && "h-20",
          )}
        />
      </div>

      <Summary />

      <CreditCard>
        <h1 className="text-lg font-bold text-black01 dark:text-white md:text-2xl">
          Finalize seu Empréstimo
        </h1>
        <p className="mb-4 text-xs dark:text-gray-300">
          Preencha os dados finais para finalizar seu empréstimo
        </p>
        <div className="flex w-full flex-col gap-3">
          <Input
            name="email"
            label="E-mail"
            icon={<Mail className="mr-1 h-4 w-4 text-black03 dark:text-white" />}
            error={methods.formState.errors.email?.message}
            placeholder="Insira seu e-mail"
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-[60%_40%]">
            <Input
              label="RG"
              name="rg.number"
              type="text"
              onInput={rgMask}
              maxLength={9}
              error={methods.formState.errors.rg?.number?.message}
            />
            <Select
              label="UF de emissão"
              name="rg.uf"
              options={uf}
              placeholder="Selecione"
              error={methods.formState.errors.rg?.uf?.message}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[60%_40%]">
            <Select
              label="Orgão de emissão"
              name="rg.org"
              options={orgs}
              placeholder="Selecione o Órgão de emissão"
              error={methods.formState.errors.rg?.org?.message}
            />
            <Input
              label="Data de emissão"
              name="rg.date"
              type="text"
              inputMode="numeric"
              maxLength={10}
              onInput={dateMask}
              placeholder="dd/mm/aaaa"
              error={methods.formState.errors.rg?.date?.message}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2">
            <Input
              name="address.zipCode"
              disabled={type === PartnerType.CREFAZ}
              label="CEP"
              onBlur={(e) => handleCepRequest(e.target.value)}
              maxLength={9}
              icon={<MapPin className="dark:text-white" height={15} width={16} />}
              error={methods.formState.errors.address?.zipCode?.message}
              placeholder="Insira seu CEP"
              onInput={zipCodeMask}
            />
          </div>

          <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-[50%_50%]">
            <Input
              name="address.street"
              label="Endereço"
              error={methods.formState.errors.address?.street?.message}
              placeholder="Insira seu endereço"
            />
            <Input
              className="flex-1"
              name="address.number"
              label="Número"
              error={methods.formState.errors.address?.number?.message}
              placeholder=""
            />
          </div>

          <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-[50%_50%]">
            <Input
              className="flex-1"
              name="address.complement"
              label="Complemento"
              error={methods.formState.errors.address?.complement?.message}
              placeholder="Insira seu complemento"
            />
            <Input
              className="flex-1"
              name="address.neighborhood"
              label="Bairro"
              error={methods.formState.errors.address?.neighborhood?.message}
              placeholder="Insira seu bairro"
            />
          </div>

          <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[50%_50%]">
            <Input
              label="Cidade"
              name="address.city"
              type="text"
              error={methods.formState.errors.address?.city?.message}
            />
            <Select
              label="Estado"
              name="address.state"
              options={states}
              placeholder="Selecione o seu estado"
              error={methods.formState.errors.address?.state?.message}
            />
          </div>

          {isCrefaz && (
            <>
              <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[50%_50%]">
                <Select
                  label="Gênero"
                  name="gender"
                  options={genderOptions}
                  placeholder="Selecione"
                  error={(methods.formState.errors as any)?.gender?.message}
                />
                <Select
                  label="Estado civil"
                  name="maritalStatus"
                  options={maritalStatusOptions}
                  placeholder="Selecione"
                  error={
                    (methods.formState.errors as any)?.maritalStatus?.message
                  }
                />
              </div>
              <Input
                label="Nome do Cônjuge"
                name="spouseName"
                placeholder="Insira o nome do cônjuge"
                disabled={!requiresSpouseName}
                error={(methods.formState.errors as any)?.spouseName?.message}
              />
              <Input
                label="Nome da mãe"
                name="motherName"
                placeholder="Insira o nome da mãe"
                error={(methods.formState.errors as any)?.motherName?.message}
              />
              <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[50%_50%]">
                <Input
                  label="Cidade de naturalidade"
                  name="placeOfBirth.city"
                  type="text"
                  error={methods.formState.errors.placeOfBirth?.city?.message}
                />
                <Select
                  label="UF de naturalidade"
                  name="placeOfBirth.uf"
                  options={uf}
                  placeholder="Selecione"
                  error={methods.formState.errors.placeOfBirth?.uf?.message}
                />
              </div>
            </>
          )}

          {isCrefaz && (
            <div className="flex flex-col gap-3">
              <p className="text-left text-sm font-semibold text-black01 dark:text-white">
                Referências pessoais
              </p>
              {fields.map((field, index) => {
                const refErrors = (
                  methods.formState.errors.contactReferences as any
                )?.referenceContact?.[index];
                return (
                  <div
                    key={field.id}
                    className="grid grid-cols-1 items-start gap-3 md:grid-cols-3"
                  >
                    <Select
                      name={`contactReferences.referenceContact.${index}.degree`}
                      label="Grau de parentesco"
                      options={relationshipDegreeOptions}
                      placeholder="Selecione"
                      error={refErrors?.degree?.message}
                    />
                    <Input
                      name={`contactReferences.referenceContact.${index}.name`}
                      label="Nome"
                      placeholder="Insira o nome"
                      error={refErrors?.name?.message}
                    />
                    <Input
                      name={`contactReferences.referenceContact.${index}.phone`}
                      label="Telefone"
                      placeholder="Insira o telefone"
                      onInput={phoneMask}
                      maxLength={15}
                      error={refErrors?.phone?.message}
                    />
                  </div>
                );
              })}
            </div>
          )}

          <hr className="my-2 h-[3px] w-full bg-primary" />

          <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-2">
            <Select
              name="bankAccount.bank"
              label="Banco"
              labelIcon={
                <Landmark
                  width={14}
                  height={16}
                  className="mr-1 fill-black03 text-black03 dark:fill-white dark:text-white"
                />
              }
              options={type === PartnerType.CREFAZ ? banksCrefaz : bankOptions}
              placeholder="Selecione"
              error={methods.formState.errors.bankAccount?.bank?.message}
            />
            <Input
              name="bankAccount.agency"
              label="Agência"
              error={methods.formState.errors.bankAccount?.agency?.message}
              placeholder="Insira sua agência"
              inputMode="numeric"
              maxLength={4}
            />
          </div>

          <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-2">
            <Select
              name="bankAccount.accountType"
              label="Tipo de conta"
              options={[
                { label: "Conta Corrente", value: "CC" },
                { label: "Conta Poupança", value: "CP" },
              ]}
              placeholder="Selecione "
              error={methods.formState.errors.bankAccount?.accountType?.message}
            />
            <Input
              name="bankAccount.account"
              label="Conta C/ Digito"
              error={methods.formState.errors.bankAccount?.account?.message}
              placeholder="Insira sua conta"
              inputMode="numeric"
              maxLength={11}
            />
          </div>

          <Button
            isLoading={isLoading}
            onClick={handleSubmit}
            className="ml-auto mt-2 w-max rounded-xl px-14 font-semibold text-white"
          >
            Finalizar
          </Button>
        </div>
      </CreditCard>
    </div>
  );
};
